import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

import { PycnometerProfilesService } from 'src/app/services/pycnometer-profiles.service';
import { PycnometerProfile, SavePycnometerProfile } from '@models/pycnometer-profile.interface';

/** Emitted when a save completes so the parent can refresh and re-select. */
export interface ProfileSavedEvent {
  id: number;
}

@Component({
  selector: 'app-pycnometer-profile-detail',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './pycnometer-profile-detail.component.html',
  styleUrl: './pycnometer-profile-detail.component.scss'
})
export class PycnometerProfileDetailComponent implements OnChanges, OnDestroy {
  @Input() profile: PycnometerProfile | null = null;

  @Output() saved = new EventEmitter<ProfileSavedEvent>();

  private readonly profilesService = inject(PycnometerProfilesService);
  private readonly fb = inject(FormBuilder);
  private saveRequest?: Subscription;

  protected isSaving = signal(false);
  protected errorMessage = signal('');

  protected form = this.fb.group({
    name:            ['', [Validators.required, Validators.maxLength(50)]],
    referenceVolume: [null as number | null, [Validators.required, Validators.min(0.0000000001)]],
    cellVolume:      [null as number | null, [Validators.required, Validators.min(0.0000000001)]]
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['profile']) return;

    if (this.profile) {
      this.form.patchValue({
        name:            this.profile.name ?? '',
        referenceVolume: this.profile.referenceVolume,
        cellVolume:      this.profile.cellVolume
      });
    } else {
      this.form.reset();
    }

    this.errorMessage.set('');
    this.form.enable();
  }

  ngOnDestroy(): void {
    this.saveRequest?.unsubscribe();
  }

  get isNew(): boolean {
    return !this.profile || this.profile.id === 0;
  }

  protected focusNextInput(event: KeyboardEvent): void {
    const form = event.currentTarget as HTMLFormElement;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const editableInputs = Array.from(form.querySelectorAll<HTMLElement>(
      'input[formControlName]:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([readonly]):not([disabled]):not([tabindex="-1"])'
    ));
    const currentIndex = editableInputs.indexOf(target);
    if (currentIndex < 0) return;

    event.preventDefault();
    if (currentIndex < editableInputs.length - 1) {
      editableInputs[currentIndex + 1].focus();
    }
  }

  protected onSave(): void {
    if (this.form.invalid || this.isSaving()) return;

    const raw = this.form.getRawValue();
    const dto: SavePycnometerProfile = {
      name:            raw.name!.trim(),
      referenceVolume: raw.referenceVolume!,
      cellVolume:      raw.cellVolume!
    };

    this.isSaving.set(true);
    this.errorMessage.set('');
    this.clearServerErrors();

    const request$ = this.isNew
      ? this.profilesService.create(dto)
      : this.profilesService.update(this.profile!.id, dto);

    this.saveRequest = request$.subscribe({
      next: savedProfile => {
        this.isSaving.set(false);
        this.saved.emit({ id: savedProfile.id });
      },
      error: (err: HttpErrorResponse) => {
        this.isSaving.set(false);
        this.handleServerError(err);
      }
    });
  }

  private handleServerError(err: HttpErrorResponse): void {
    const body = err.error;

    // Map field-level errors from ValidationProblem response onto form controls.
    if (body?.errors && typeof body.errors === 'object') {
      const errors = body.errors as Record<string, string[]>;

      const fieldMap: Record<string, string> = {
        name:            'name',
        referenceVolume: 'referenceVolume',
        cellVolume:      'cellVolume'
      };

      let mapped = false;
      for (const [serverField, messages] of Object.entries(errors)) {
        const formField = fieldMap[serverField.toLowerCase()] ?? serverField.toLowerCase();
        const control = this.form.get(formField);
        if (control) {
          control.setErrors({ serverError: messages[0] });
          control.markAsTouched();
          mapped = true;
        }
      }

      if (!mapped) {
        this.errorMessage.set('Save failed. Please check your entries and try again.');
      }
    } else {
      this.errorMessage.set('An unexpected error occurred. Please try again.');
    }
  }

  private clearServerErrors(): void {
    for (const key of Object.keys(this.form.controls)) {
      const control = this.form.get(key);
      if (control?.hasError('serverError')) {
        const { serverError: _, ...rest } = control.errors ?? {};
        control.setErrors(Object.keys(rest).length > 0 ? rest : null);
      }
    }
  }
}
